// generic client
/* eslint-disable import/prefer-default-export */

import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import LeanplumUtils from 'utils/leanplum';
import { useMutation } from '@apollo/client/react';
import { useToast } from 'utils/hooks/useToast/useToast';
import JwtManager from 'lib/JwtManager';
import useMe from 'utils/hooks/useMe/useMe';
import SendLeanplumDeviceId from './SendLeanplumDeviceId.graphql';

export const useLeanplum = () => {
  const { t } = useTranslation(['LAYOUT', 'STATIC']);
  const { toast } = useToast();
  const { me } = useMe();
  const [sendLeanplumDeviceId] = useMutation(SendLeanplumDeviceId);

  const [leanplumState, setLeanplumState] = useState({
    isLeanplumAvailable: false,
    isWebPushSupported: false,
    isWebPushSubscribed: false,
    inbox: undefined,
  });

  const [notifications, setNotifications] = useState([]);

  const onNotificationsUpdate = (notins) => {
    setNotifications(notins);
  };

  const toggleWebPushSub = async () => {
    // Sync effect of having the switch toggled.
    const currentLeanplumState = LeanplumUtils.getLeanplumState();

    setLeanplumState({
      ...currentLeanplumState,
      isWebPushSubscribed: !currentLeanplumState.isWebPushSubscribed,
    });

    try {
      await LeanplumUtils.toggleWebPushSuscription();
    } catch {
      toast({
        autoClose: true,
        autoCloseDuration: 3000,
        message: t('LAYOUT:NOTIFICATION_PUSH_SUBSCRIBE_ERR'),
        title: t('STATIC:SOMETHING_WENT_WRONG'),
        type: 'error',
      });
    }

    setLeanplumState(LeanplumUtils.getLeanplumState());
  };

  // Setup (only Once)
  useEffect(() => {
    const setupLeanplum = async () => {
      // if is not ready yet
      await LeanplumUtils.initLeanplum();
      LeanplumUtils.leanplumInboxSubscribe(onNotificationsUpdate);
      setLeanplumState(LeanplumUtils.getLeanplumState());
    };
    setupLeanplum();
    return () => {
      LeanplumUtils.tearDown();
    };
  }, []);

  // On User
  useEffect(() => {
    const sendDeviceId = async () => {
      try {
        const params = LeanplumUtils.getLeanplumParameters();
        await sendLeanplumDeviceId({ variables: params });
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    if (leanplumState.isLeanplumAvailable && me?.id && JwtManager.jwt) {
      // set new user in leanplum and crearte a subscription
      LeanplumUtils.setLeanplumUser(me);

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLeanplumState(LeanplumUtils.getLeanplumState());
      sendDeviceId();
    }
  }, [me, leanplumState.isLeanplumAvailable, sendLeanplumDeviceId]);

  return {
    manager: LeanplumUtils,
    enabledPushNotifications: leanplumState.isWebPushSubscribed,
    togglePushNotifications: toggleWebPushSub,
    notifications,
    isWebPushSupported: leanplumState.isWebPushSupported,
  };
};
